import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import * as dayjs from 'dayjs';
import { UserAccountHomeDto, UserAccountRegDto, WebDeliveryTestTakerStateDto } from 'src/app/core/dtos/dto';
import { RegBlobDataTypeEnum, ScannedDocumentStatusEnum } from 'src/app/core/dtos/enum';
import { AccountService } from 'src/app/core/services/account.service';
import { HttpErrorResponseMessages } from 'src/app/core/dtos/http-error-response-messages';
import { HotToastService } from '@ngneat/hot-toast';

@Component({
  selector: 'app-start-my-test',
  templateUrl: './start-my-test.component.html',
  styleUrls: ['./start-my-test.component.scss']
})

export class StartMyTestComponent implements OnInit {
  @ViewChild('content') modalContent: string;

  @Input() myAccount: UserAccountHomeDto;
  @Input() isUpcomingTest: boolean;
  @Input() reg: UserAccountRegDto;

  checkingUpcomingWebDeliveryTestState = false;
  firstFileUploaded = false;

  photoDueDate: string;
  photoDueDate24hour: string;
  idDueDate: string;
  idDueDate24hour: string;
  testDueDate: string;
  testStartTimeDisplay: string;
  testDisplayTime: string;
  testAvailableToStart: boolean;
  modalBody: string;
  modalTitle: string;
  upcomingWebDeliveryTestState: WebDeliveryTestTakerStateDto;
  dueDates: Record<string, string>;
  showPhotoCapture: boolean;

  constructor(private accountService: AccountService,
    private modalService: NgbModal,
    private toastService: HotToastService,) { }

  ngOnInit(): void {
    this.setDates();
    if (this.isUpcomingTest) {
      this.getWebDeliveryRegStatus(this.reg.regId);
    }
  }

  public get ScannedDocumentStatusEnum() {
    return ScannedDocumentStatusEnum;
  }

  public get RegBlobDataTypeEnum() {
    return RegBlobDataTypeEnum;
  }

  changeStatus() {
    this.firstFileUploaded = true;
  }

  convertUTCDateToLocalDate(date: Date): Date {
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds()));
  }

  copyConfirmationNumber() {
    const text_to_share = this.myAccount.proctorConfirmationNumber;

    // create temp element
    const copyElement = document.createElement("span");
    copyElement.appendChild(document.createTextNode(text_to_share));
    copyElement.id = 'tempCopyToClipboard';
    document.body.append(copyElement);

    // select the text
    const range = document.createRange();
    range.selectNode(copyElement);
    window.getSelection().removeAllRanges();
    window.getSelection().addRange(range);

    // copy & cleanup
    document.execCommand('copy');
    window.getSelection().removeAllRanges();
    copyElement.remove();
    this.toastService.success("Copied to clipboard");
  }

  getLocalTimeZone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  formatTestDateTime(dateTime: Date): string {
    return dayjs(dateTime).format('MMM D, YYYY - dddd h:mm a');
  }

  getDueDate(testDateTimeUtc: Date, daysAheadOfTestTime: number): string {
    const dueDate = this.convertUTCDateToLocalDate(new Date(testDateTimeUtc));
    dueDate.setDate(dueDate.getDate() - daysAheadOfTestTime);
    return dayjs(dueDate).format('dddd, MMM D, YYYY');
  }

  getDueDate24HourTime(testDateTimeUtc: Date, daysAheadOfTestTime: number): string {
    const dueDate = this.convertUTCDateToLocalDate(new Date(testDateTimeUtc));
    dueDate.setDate(dueDate.getDate() - daysAheadOfTestTime);
    return dayjs(dueDate).format('h:mm a');
  }

  getTestStartTimeDisplay(testDateTimeUtc: Date): string {
    const checkedInDateTime = this.convertUTCDateToLocalDate(new Date(testDateTimeUtc));
    checkedInDateTime.setMinutes(checkedInDateTime.getMinutes());
    return dayjs(checkedInDateTime).format('h:mm a');
  }

  getTestTimeDisplay(testDateTimeUtc: Date): string {
    const testTime = this.convertUTCDateToLocalDate(new Date(testDateTimeUtc));
    return dayjs(testTime).format('h:mm a') + " on " + dayjs(testTime).format('dddd') + ", " + dayjs(testTime).format('MMMM  DD, YYYY');
  }

  getWebDeliveryRegStatus(regId: number) {
    this.accountService.getWebDeliveryRegStatus(regId).subscribe(result => {
      if (result) {
        this.upcomingWebDeliveryTestState = result.content;
      }
      this.testAvailableToStart = this.isOnlineTestTimeWindowOpenForStart() || this.isOnlineTestStateOpenForStart();
    },
      (error: HttpErrorResponse) => {
        if (error.status === 400) {
          const errorMessage = error.error as HttpErrorResponseMessages;
          errorMessage.reasons.forEach(reasons => {
            this.toastService.warning(reasons.errorMessage);
          });
        }
      });
  }

  goToWebDelivery() {
    if (this.myAccount.webDeliveryUrl) {
      window.open(this.myAccount.webDeliveryUrl);
      return;
    }
  }

  isOnlineTestTimeWindowOpenForStart() {
    if (this.reg && this.reg.testDateTimeUtc) {
      const testDate = dayjs(this.convertUTCDateToLocalDate(new Date(this.reg.testDateTimeUtc)));
      const now = dayjs(new Date());
      return (now > testDate && now.diff(testDate) < 30 * 60 * 1000); // available to start 30 minutes after test start time?
    }
    else {
      return false;
    }
  }

  isOnlineTestStateOpenForStart(): boolean {
    if (this.upcomingWebDeliveryTestState) {
      return ((this.upcomingWebDeliveryTestState.checkedIn === true || this.upcomingWebDeliveryTestState.testStarted === true) && !this.upcomingWebDeliveryTestState.testEnded);
    }
    else {
      return false;
    }
  }

  setDates() {
    this.photoDueDate = this.getDueDate(this.reg.testDateTimeUtc, 2);
    this.photoDueDate24hour = this.getDueDate24HourTime(this.reg.testDateTimeUtc, 2);
    this.idDueDate = this.getDueDate(this.reg.testDateTimeUtc, 2);
    this.idDueDate24hour = this.getDueDate24HourTime(this.reg.testDateTimeUtc, 2);
    this.testDueDate = this.getDueDate(this.reg.testDateTimeUtc, 0);
    this.testStartTimeDisplay = this.getTestStartTimeDisplay(this.reg.testDateTimeUtc);
    this.testDisplayTime = this.getTestTimeDisplay(this.reg.testDateTimeUtc);
  }

  openModal(showPhotoCapture: boolean) {
    this.showPhotoCapture = showPhotoCapture;
    this.modalService.open(this.modalContent, { size: 'lg' });
  }

  changeUploadedPhotoStatus() {
    console.log("photo uploaded");
    window.location.reload();
  }

  startWebDelivery() {
    this.checkingUpcomingWebDeliveryTestState = true;
    this.accountService.webDeliveryStartTestAttempt(this.reg.regId)
      .subscribe(() => {
      },
        (error: HttpErrorResponse) => {
          this.checkingUpcomingWebDeliveryTestState = false;
          if (error.status === 400) {
            const errorMessage = error.error as HttpErrorResponseMessages;
            errorMessage.reasons.forEach(reasons => {
              this.toastService.warning(reasons.errorMessage);
            });
          }
        });

    this.accountService.getWebDeliveryRegStatus(this.reg.regId).subscribe(result => {
      if (result) {
        this.upcomingWebDeliveryTestState = result.content;
      }
      this.checkingUpcomingWebDeliveryTestState = false;
      if (!this.upcomingWebDeliveryTestState?.checkedIn && !this.testAvailableToStart) {
        this.toastService.warning("Test start time has expired.  Unfortunately, your test start time has passed and you can no longer check in. Please contact us for help if this is unexpected.");
      }
      else if (this.upcomingWebDeliveryTestState?.testEnded) {
        this.toastService.warning("Test Finished.  Your test is now finished and you cannot access it further. Please contact us for help if this is unexpected.");
      }
      else {
        this.goToWebDelivery();
      }
    },
      (error: HttpErrorResponse) => {
        this.toastService.warning("Test Not Accessible.  Your test cannot be accessed. Please contact us for help if this is unexpected.");
        if (error.status === 400) {
          const errorMessage = error.error as HttpErrorResponseMessages;
          errorMessage.reasons.forEach(reasons => {
            this.toastService.warning(reasons.errorMessage);
          });
        }
      });
  }
}
